import { useState } from "react";
import { Navbar, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Phone, Mail, MapPin, Clock, Send, CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "(405) 256-2176",
      link: "tel:4052562176"
    },
    {
      icon: Mail,
      title: "Email",
      content: "ygkconstruction@gmail.com",
      link: "mailto:ygkconstruction@gmail.com"
    },
    {
      icon: MapPin,
      title: "Office",
      content: "Oklahoma City, Oklahoma",
      link: null
    },
    {
      icon: Clock,
      title: "Hours",
      content: "Mon-Fri: 8am - 6pm",
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-black pt-32 pb-20 relative overflow-hidden">
        {/* Background logo */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: '300px',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.05,
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Get In Touch</p>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-6">
              Contact <span className="text-orange-500">Us</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Have questions about your project? Ready to get started? 
              We're here to help. Reach out and let's talk.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-zinc-950 bg-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Let's Connect</h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-10">
                Whether you have a question about our services, need a quote, or want to 
                discuss a potential project, we're ready to hear from you.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                {contactInfo.map((item, index) => (
                  <Card key={index} className="bg-zinc-900/50 border border-zinc-800 hover:border-orange-500/50 transition-colors backdrop-blur-sm">
                    <CardContent className="p-6">
                      <item.icon className="h-8 w-8 text-orange-500 mb-4" />
                      <h3 className="font-bold text-white mb-1">{item.title}</h3>
                      {item.link ? (
                        <a 
                          href={item.link}
                          className="text-gray-400 hover:text-orange-400 transition-colors"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-gray-400">{item.content}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Service Area */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm">
                <h3 className="font-bold text-white mb-4">Service Area</h3>
                <p className="text-gray-400 mb-4">
                  We proudly serve Oklahoma City and the surrounding areas including:
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Oklahoma City", "Edmond", "Norman", "Moore", "Midwest City", "Del City", "Yukon", "Mustang"].map((area) => (
                    <span key={area} className="px-3 py-1 bg-zinc-800 border border-zinc-700 text-sm text-gray-300">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <Card className="bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-10 w-10 text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                    <p className="text-gray-400 mb-8">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                      }}
                      variant="outline"
                      className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                    >
                      Send Another Message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-white mb-6">Send Us a Message</h3>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                            Name *
                          </Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Your name"
                            className="py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                            Phone
                          </Label>
                          <Input
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="(405) 555-0123"
                            className="py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                          Email *
                        </Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="your@email.com"
                          className="py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                          Subject
                        </Label>
                        <Input
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          placeholder="How can we help?"
                          className="py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Tell us about your project..."
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-orange-500 min-h-[150px]"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-lg btn-glow"
                      >
                        {submitting ? "Sending..." : "Send Message"}
                        <Send className="ml-2 h-5 w-5" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
